// P10 — what the current scrubber step lets you inject.
//
// The scrubber accumulates the extended capture pins (select run-sets, injected
// failures, injected delays) as OPT-IN annotations on a step — unlike the if/try
// gate (which forces an exclusive path pick), these never block stepping; a
// select runs all cases and a step succeeds instantly by default. This pure
// resolver reports, for the active step, which injections apply: a delay + a
// failure on any real call/await hop, and the case run-set of an enclosing
// `select`. The panel renders the matching controls; the mutators
// (`setFailure`/`setDelay`/`setSelectRuns`) write them into the walker overlay.

import type { NodeId, WalkNode, WalkResult } from "@archlang/engine";

/** The `select` frame enclosing the active step — its cases + how many run. */
export interface SelectFrameTarget {
  readonly nodeId: NodeId;
  readonly head?: string;
  /** Case labels in declaration order (the run-set is a subset of these). */
  readonly cases: readonly string[];
  /** `select N` / `select one` bound, or undefined for a bare `select` (all). */
  readonly runCount?: number;
}

/** The injections available at the active step. */
export interface StepInjectTargets {
  /** The active step's nodeId — the overlay key for its failure/delay answer. */
  readonly nodeId: NodeId;
  /** The callee path (`step.to`) — the `FailureAnswer`/`DelayAnswer` ref, i.e.
   *  the `<Callee.step>` a `fails`/`after`/`takes` pin names. */
  readonly to: readonly string[];
  /** True for a real call/await hop (failures + delays apply); false for a note,
   *  prose row, or `go` re-entry (no hop to fail or time). */
  readonly failable: boolean;
  /** The enclosing `select` frame, when the step sits inside one. */
  readonly selectFrame?: SelectFrameTarget;
}

/** Resolve the active step's injection targets, or null when no step is active
 *  (or the held index is off the current walk). */
export function injectTargetsFor(walk: WalkResult | null, stepIndex: number | null): StepInjectTargets | null {
  if (!walk || stepIndex === null) return null;
  const step = walk.byIndex.get(stepIndex);
  if (!step) return null;

  // Find the nearest enclosing `select` (switch) frame by walking the tree to
  // the step. Exclusive frames (if/try) and parallels are transparent here.
  let selectFrame: SelectFrameTarget | undefined;
  const visit = (nodes: readonly WalkNode[], encl: SelectFrameTarget | undefined): boolean => {
    for (const n of nodes) {
      switch (n.kind) {
        case "step":
          if (n.stepIndex === stepIndex) {
            selectFrame = encl;
            return true;
          }
          break;
        case "switch": {
          const frame: SelectFrameTarget = {
            nodeId: n.nodeId,
            ...(n.head !== undefined ? { head: n.head } : {}),
            cases: n.cases.map((c) => c.label),
            ...(n.runCount !== undefined ? { runCount: n.runCount } : {}),
          };
          for (const c of n.cases) if (visit(c.children, frame)) return true;
          break;
        }
        case "if":
          for (const b of n.branches) if (visit(b.children, encl)) return true;
          if (n.elseBranch && visit(n.elseBranch.children, encl)) return true;
          break;
        case "try":
          if (visit(n.tryBranch.children, encl)) return true;
          for (const c of n.catches) if (visit(c.children, encl)) return true;
          break;
        case "parallel":
          for (const b of n.branches) if (visit(b.children, encl)) return true;
          break;
        case "each":
          if (visit(n.children, encl)) return true;
          break;
        case "do":
          if (n.body && visit(n.body, encl)) return true;
          break;
        case "terminator":
          break;
      }
    }
    return false;
  };
  visit(walk.tree, undefined);

  const failable = step.noteText === undefined && !step.prose && step.reentryTarget === undefined;
  return {
    nodeId: step.nodeId,
    to: step.to,
    failable,
    ...(selectFrame ? { selectFrame } : {}),
  };
}
